import React, {useState, useEffect} from "react"
import {
	Box,
	Button,
	Grid,
	TextField,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	CircularProgress,
	Chip,
} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import CloseIcon from "@mui/icons-material/Close"

import {useFileUpload, FileUpload} from "../../hooks/useFileUpload"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type FieldType = "textarea" | "number" | string

type Field = {
	Name: string
	Type: FieldType
	Required: boolean
}

type FieldAnswered = {
	Name: string
	Type: FieldType
	Content: string | number
}

type Form = {
	Type: string
	Version: number
	Fields: Field[]
}

type Upload = {
	ID: string
	FileName: string
}

type NoteFormProps = {
	customerID: string
	forms: Form[]
	disabled?: boolean
	onSuccess?: () => void
	attachmentsEnabled: boolean
}

const isTextareaField = (field: Field | FieldAnswered) => field.Type === "textarea"
const isNumberField = (field: Field | FieldAnswered) => field.Type === "number"

function NoteForm({customerID, forms, disabled = false, onSuccess, attachmentsEnabled}: NoteFormProps) {
	const FILE_INPUT_ID = "file-upload"
	const {notification} = useNotification()
	const [agentEmail, agentName] = useAgent()
	const [selectedFormType, setSelectedFormType] = useState<string>("")
	const [fieldValues, setFieldValues] = useState<Record<string, string | number>>({})
	const [submitting, setSubmitting] = useState(false)
	const {fileUploads, uploadFiles, removeFile, clearFiles, isUploading} = useFileUpload(customerID)

	const getUploadChipColor = (upload: FileUpload) => {
		if (upload.uploaded) return "primary"
		if (upload.error) return "secondary"
		return "default"
	}

	useEffect(() => {
		if (forms.length === 1) {
			setSelectedFormType(forms[0].Type)
		}
	}, [forms])

	const selectedForm = forms.find((f) => f.Type === selectedFormType)

	const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files
		if (!files || files.length === 0) return

		await uploadFiles(Array.from(files))
	}

	const handleFieldChange = (fieldName: string, value: string | number) => {
		setFieldValues((prev) => ({
			...prev,
			[fieldName]: value,
		}))
	}

	const hasContent = (answer: FieldAnswered) => answer.Content !== "" && answer.Content !== 0

	const isFormValid = () => {
		if (!selectedForm) return false
		const requiredFields = selectedForm.Fields.filter((f) => f.Required)
		return requiredFields.every((field) => fieldValues[field.Name])
	}

	const buildNotePayload = () => {
		if (!selectedForm) return null

		const allAnswers: FieldAnswered[] = selectedForm.Fields.map((field) => ({
			Name: field.Name,
			Type: field.Type,
			Content: fieldValues[field.Name] || (isNumberField(field) ? 0 : ""),
		}))

		const answers = allAnswers.filter(hasContent)

		const uploads: Upload[] = fileUploads
			.filter((u) => u.uploaded && u.uploadID)
			.map((u) => ({
				ID: u.uploadID,
				FileName: u.file.name,
			}))

		return {
			FormType: selectedForm.Type,
			FormVersion: selectedForm.Version,
			Answers: answers,
			Uploads: uploads,
			AgentName: agentName,
			AgentEmail: agentEmail,
		}
	}

	const resetForm = () => {
		setFieldValues({})
		clearFiles()
		if (forms.length > 1) {
			setSelectedFormType("")
		}
	}

	const validateForm = (): string | null => {
		if (!selectedForm) return "No form selected"

		const missingFields = selectedForm.Fields.filter((field) => field.Required && !fieldValues[field.Name])
		if (missingFields.length > 0) {
			return `Please fill in required fields: ${missingFields.map((f) => f.Name).join(", ")}`
		}

		if (isUploading) {
			return "Please wait for file uploads to complete"
		}

		return null
	}

	const handleAddNote = async () => {
		const validationError = validateForm()
		if (validationError) {
			notification.error(validationError)
			return
		}

		const payload = buildNotePayload()
		if (!payload) return

		setSubmitting(true)

		try {
			await api.customer.createNote(customerID, payload)
			notification.success("Note added successfully")
			resetForm()

			if (onSuccess) {
				onSuccess()
			}
		} catch (error: any) {
			notification.error(error.Message || "Failed to add note")
		} finally {
			setSubmitting(false)
		}
	}

	const canSubmit = !submitting && !disabled && selectedForm && isFormValid() && !isUploading

	return (
		<Grid direction="column" spacing={2} container>
			{/* Form Type Selector (only show if multiple forms) */}
			{forms.length > 1 && (
				<Grid>
					<FormControl variant="outlined" fullWidth disabled={disabled}>
						<InputLabel>Form Type</InputLabel>
						<Select
							value={selectedFormType}
							onChange={(e) => {
								setSelectedFormType(e.target.value as string)
								setFieldValues({})
							}}
							label="Form Type"
						>
							<MenuItem value="">
								<em>Select a form type</em>
							</MenuItem>
							{forms.map((form) => (
								<MenuItem key={form.Type} value={form.Type}>
									{form.Type}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			)}

			{/* Dynamic Form Fields */}
			{selectedForm &&
				selectedForm.Fields.map((field) => (
					<Grid key={field.Name}>
						<TextField
							label={field.Name + (field.Required ? " *" : "")}
							multiline={isTextareaField(field)}
							minRows={isTextareaField(field) ? 4 : undefined}
							maxRows={isTextareaField(field) ? 50 : undefined}
							type={isNumberField(field) ? "number" : "text"}
							placeholder={
								disabled
									? "Notes are disabled for Latitude customers"
									: `Enter ${field.Name.toLowerCase()}`
							}
							variant="outlined"
							fullWidth
							value={fieldValues[field.Name] || ""}
							onChange={(e) =>
								handleFieldChange(
									field.Name,
									isNumberField(field) ? parseFloat(e.target.value) || 0 : e.target.value,
								)
							}
							disabled={disabled}
						/>
					</Grid>
				))}

			{/* File Upload */}
			{selectedForm && attachmentsEnabled && (
				<Grid>
					<Box display="none">
						<input
							accept="*/*"
							id={FILE_INPUT_ID}
							multiple
							type="file"
							onChange={handleFileSelect}
							disabled={disabled}
						/>
					</Box>
					<label htmlFor={FILE_INPUT_ID}>
						<Button variant="outlined" component="span" startIcon={<AttachFileIcon />} disabled={disabled}>
							Attach Files
						</Button>
					</label>

					{fileUploads.length > 0 && (
						<Box mt={2}>
							<Grid container spacing={1}>
								{fileUploads.map((upload, idx) => (
									<Grid key={idx}>
										<Chip
											label={upload.file.name}
											onDelete={upload.uploading ? undefined : () => removeFile(upload.file)}
											deleteIcon={
												upload.uploading ? <CircularProgress size={16} /> : <CloseIcon />
											}
											color={getUploadChipColor(upload)}
											icon={
												upload.uploading ? <CircularProgress size={16} /> : <AttachFileIcon />
											}
										/>
									</Grid>
								))}
							</Grid>
						</Box>
					)}
				</Grid>
			)}

			{/* Submit Button */}
			<Grid>
				<Grid container justifyContent="flex-end" spacing={2}>
					<Grid>
						<Button disabled={!canSubmit} onClick={handleAddNote}>
							{submitting ? <CircularProgress size={24} /> : "Add Note"}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export {NoteForm}
export type {Form, Field, FieldAnswered, Upload}
